import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Grid, IconButton, Tooltip, InputAdornment} from "@mui/material";
import {useQuery } from "@tanstack/react-query";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import {CheckCircle, DeleteRounded, PriorityHighRounded} from "@mui/icons-material";
import {FormAutocomplete, FormDatePicker, FormTextField} from "../../../../common/form-fields-wrappers";
import {GENDER_LIST, ITALY_CODE} from "../../../../config";
import {IBillingOwnerPerson, IRaFillingForm} from "../../../../models/models";
import {fetchPrefixes, fetchProvincesByCountryCode} from "../../../services/http.services";
import {bookingDataValueState, driverResidenceCountriesListState} from "../../../state";
import GoogleAutocompleteWrapper from "../../../../../../util/component/GoogleAutocompleteWrapper";
import {TGenericValidateType, genericValidate, getDataFromApiGoogle, checkSpecialCaseItProvince} from "../../../../utils";
import {DEFAULT_BO_FIELDS_STATE_VALUES, RA_FILLING_FORM_INITIAL_VALUE} from "../../config";
import {useDebounce} from "../../../../hooks";
import {IBillingPersonData} from "./types";

const BillingPersonData = ({type, editable, findCustomerByUniqueDataQuery, forceReadOnly }: IBillingPersonData) => {
	const { t } = useTranslation(['network', 'translation']);
	const countriesList = useRecoilValue(driverResidenceCountriesListState);
	const bookingDataValue = useRecoilValue(bookingDataValueState);
	const raFillingDataForm = useFormContext<IRaFillingForm>();

	const [billingPerson, setBillingPerson] = useState(DEFAULT_BO_FIELDS_STATE_VALUES.person);
	const billingPersonInitialized = useRef(false);

	const [billingCountry, customerId, cfValidated] =
		useWatch<IRaFillingForm, [
			`billingOwnerInfo.${typeof type}.person.billingCountry`,
			`billingOwnerInfo.${typeof type}.person.customerId`,
			`billingOwnerInfo.${typeof type}.person.cfValidated`,
		]>({
			name: [
				`billingOwnerInfo.${type}.person.billingCountry`,
				`billingOwnerInfo.${type}.person.customerId`,
				`billingOwnerInfo.${type}.person.cfValidated`
			]
		});

	const [boRr]: any = useWatch({control: raFillingDataForm.control, name: [`billingOwnerInfo.${type}.person`]});

	const [birthPlace, setBirthPlace] = useState<any>(undefined);
	const [billingPlace, setBillingPlace] = useState<any>(undefined);

	const [birthNationDisable, setBirthNationDisable] = useState<boolean>(true);
	const [billingCountryDisable, setBillingCountryDisable] = useState<boolean>(true);
	const [billingCityDisable, setBillingCityDisable] = useState<boolean>(true);
	const [billingAddressDisable, setBillingAddressDisable] = useState<boolean>(true);
	const [billingStreetNumberDisable, setBillingStreetNumberDisable] = useState<boolean>(true);
	const [billingPostcodeDisable, setBillingPostcodeDisable] = useState<boolean>(true);
	const [billingProvinceDisable, setBillingProvinceDisable] = useState<boolean>(true);
	const [residenceCountryVal, setResidenceCountryVal] = useState<string>('');
	const [val, setVal] = useState<string>('');
	const [field, setField] = useState<TGenericValidateType | null>(null);

	const setValueCheck = (field: TGenericValidateType, val: string) => {
		setField(field);
		setVal(val);
	}

	useDebounce(() => {
		if(!!field && genericValidate(field, val, residenceCountryVal)) {
			findCustomerByUniqueDataQuery.mutate({[field]: val, partnerType: '1'})
		}
	}, 1200, [field, val, residenceCountryVal]);

	const googleInputPropsBirthField = {
		onPlaceSelected: (place: any) => selectedBirthCountry(place),
		options: { fields: ['address_components'] }
	}

	const googleInputPropsBillingField = {
		onPlaceSelected: (place: any) => selectedBillingAddress(place),
		options: {
			fields: ['address_components'],
			types: ['address']
		}
	}

	/** Select birth country and set value form **/
	const selectedBirthCountry = useCallback((result : any) => {
		setBirthPlace(result);
	}, []);

	/** dep: birthPlace, set form value field of birth **/
	useEffect(() => {
		if (birthPlace) {
			const filters = ['country', 'locality', 'administrative_area_level_3'];
			const [country, locality, administrative_area_level_3] = getDataFromApiGoogle(birthPlace?.address_components, filters);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.birthPlace`,  locality?.long_name ?? administrative_area_level_3?.long_name ?? '' );
			const countriesCode = countriesList.map((elem: any) => elem.code);
			if(countriesCode.includes(country?.short_name)) {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.birthNation`,  {code: country.short_name, description: country.short_name});
				setBirthNationDisable(true);
				return;
			}

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.birthNation`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.birthNation ?? null);
			setBirthNationDisable(false);
		}

	}, [birthPlace]);

	/** reset value birth address data */
	const handleResetBirthFields = useCallback(() => {
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.birthPlace`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.birthPlace ?? '');
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.birthNation`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.birthNation ?? null);
		setBirthNationDisable(true);
	}, [])

	/** Select billing country and set value form **/
	const selectedBillingAddress = useCallback((result : any) => {
		setBillingPlace(result);
	}, [])

	/** dep: billingPlace, set form value field of billing  **/
	useEffect(() => {
		if (billingPlace) {
			const filters = ['country', 'locality', 'route', 'street_number', 'postal_code', 'administrative_area_level_2', 'administrative_area_level_3'];
			const [country, locality, route, street_number, postal_code, administrative_area_level_2, administrative_area_level_3] = getDataFromApiGoogle(billingPlace?.address_components, filters);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingCity`, locality?.long_name ?? administrative_area_level_3?.long_name ?? '');
			setBillingCityDisable(!!locality?.long_name);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingAddress`, route?.short_name ?? '');
			setBillingAddressDisable(!!route?.short_name);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingStreetNumber`, street_number?.short_name ?? '' );
			setBillingStreetNumberDisable(!!street_number?.short_name);
			if(!!route?.short_name) {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingAddressGoogleSearch`, route?.short_name ?? '' );
			}

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingPostalCode`, postal_code?.short_name ?? '' );
			setBillingPostcodeDisable(!!postal_code?.short_name);

			// set billing Country
			const countriesCode = countriesList.map((elem: any) => elem.code);
			if(countriesCode.includes(country?.short_name)) {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingCountry`,  {code: country.short_name, description: country.short_name});
				setBillingCountryDisable(true);
				if(country.short_name === ITALY_CODE) {
					const checkedProvince = checkSpecialCaseItProvince(administrative_area_level_2?.short_name);
					raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingProvince`, {code: checkedProvince, description: checkedProvince});
					setBillingProvinceDisable(!!checkedProvince);
				} else {
					raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingProvince`,  RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingProvince ?? null);
					setBillingProvinceDisable(false);
				}

			} else {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingCountry`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingCountry ?? null);
				setBillingCountryDisable(false);
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingProvince`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingProvince ?? null);
				setBillingProvinceDisable(false);
			}
		}
	}, [billingPlace]);

	/** reset value billing address data **/
	const handleResetBillingAddressFields = useCallback(() => {
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingAddressGoogleSearch`,  '');
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingCountry`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingCountry ?? null);
		setBillingCountryDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingCity`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingCity ?? '');
		setBillingCityDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingAddress`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingAddress ?? '');
		setBillingAddressDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingStreetNumber`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingStreetNumber ?? '');
		setBillingStreetNumberDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingPostalCode`,  RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingPostalCode ?? '');
		setBillingPostcodeDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingProvince`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.person?.billingProvince ?? null);
		setBillingProvinceDisable(true);
	}, []);

	const handleResidenceCountryChange = (val: string) => {
		setResidenceCountryVal(val)
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingProvince`, null);
	}

	const handleBillingCountryChange = () => {
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.person.billingProvince`, null);
	}

	const mobilePrefixesQuery = useQuery({
		queryKey: ['ntw_mobile_prefixes'],
		queryFn: fetchPrefixes,
		select: (data) =>  data.map(p => ({code: p.country, description: p.prefix}))
	});

	const provincesQuery = useQuery({
		queryKey: ['ntw_provinces', billingCountry?.code],
		queryFn: () => {
			if (!billingCountry) {
				return null;
			}
			return fetchProvincesByCountryCode({code: billingCountry.code});
		},
		enabled: !!billingCountry,
		select: (data: any) => data.response.map((v: any) => ({code: v.codeProvince, description: v.description}))
	});

	const isPristine = (field: keyof IBillingOwnerPerson): boolean =>
		!!raFillingDataForm.getValues(`billingOwnerInfo.${type}.person.${field}`) && !raFillingDataForm.control.getFieldState(`billingOwnerInfo.${type}.person.${field}`).isDirty;

	const EndAdornemnt = useMemo(() => {
		if (typeof cfValidated === 'undefined') {
			return undefined;
		}

		if (billingCountry?.code !== ITALY_CODE) {
			return undefined;
		}

		return (
			<InputAdornment position="start">
				{!!cfValidated ? <CheckCircle color="success" /> : <PriorityHighRounded color="error" />}
			</InputAdornment>
		)
	}, [cfValidated, billingCountry]);


	useEffect(() => {
		if (billingPersonInitialized.current) {
			return;
		}

		const billingOwner: any = raFillingDataForm.getValues(`billingOwnerInfo.${type}.person`);
		if (!billingOwner) {
			return;
		}

		billingPersonInitialized.current = true;

		const disableAdressFields = !!billingOwner?.billingCountry &&
												!!billingOwner?.billingProvince &&
												!!billingOwner?.billingCity &&
												!!billingOwner?.billingAddress &&
												!!billingOwner?.billingStreetNumber &&
												!!billingOwner?.billingPostalCode;

		setBillingPerson({
			firstName: 			!!billingOwner?.firstName,
			lastName: 			!!billingOwner?.lastName,
			fiscalCode: 		!!billingOwner?.fiscalCode,
			gender: 			!!billingOwner?.gender,
			birthDate: 			!!billingOwner?.birthDate,
			birthNation: 		!!billingOwner?.birthNation,
			birthPlace: 		!!billingOwner?.birthPlace,
			countryResidence: 	!!billingOwner?.countryResidence,
			prefix: 			!!billingOwner?.prefix,
			mobile: 			!!billingOwner?.mobile,
			firstEmail: 		!!billingOwner?.firstEmail,
			certifiedEmail: 	!!billingOwner?.certifiedEmail,
			drivingLicenseNumber: !!billingOwner?.drivingLicenseNumber,
			billingAddressGoogleSearch: disableAdressFields,
			billingCountry: 			disableAdressFields,
			billingProvince: 			disableAdressFields,
			billingCity: 				disableAdressFields,
			billingAddress: 			disableAdressFields,
			billingStreetNumber: 		disableAdressFields,
			billingPostalCode: 			disableAdressFields
		});
		setBillingCountryDisable(disableAdressFields);
		setBillingProvinceDisable(disableAdressFields);
		setBillingCityDisable(disableAdressFields);
		setBillingAddressDisable(disableAdressFields);
		setBillingStreetNumberDisable(disableAdressFields);
		setBillingPostcodeDisable(disableAdressFields);
	}, [boRr])

	return (
		<>
			<Grid container columnSpacing={2}>
				<Grid container item xs={12} columnSpacing={2}>
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.firstName`}
							label={t("raFilling.billingOwnerInfo.form.label.name")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.firstName}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.lastName`}
							label={t("raFilling.billingOwnerInfo.form.label.surname")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.lastName}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.gender`}
							label={t("raFilling.billingOwnerInfo.form.label.gender")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.gender}
							options={GENDER_LIST}
							getOptionLabel={(option) => option.code}
							isOptionEqualToValue={(option, value) => option?.code === value?.code}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.fiscalCode`}
							label={t("raFilling.billingOwnerInfo.form.label.fiscalCode")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.fiscalCode}
							onChange={(e) => {setValueCheck('taxCode', e.target.value)}}
							InputProps={{endAdornment: EndAdornemnt}}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormDatePicker<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.birthDate`}
							label={t("raFilling.billingOwnerInfo.form.label.birthDate")}
							disabled={(forceReadOnly || !editable) && billingPerson.birthDate}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<Controller
							name={`billingOwnerInfo.${type}.person.birthPlace`}
							control={raFillingDataForm.control}
							render={({field: {onChange, value}}: any) => (
								<GoogleAutocompleteWrapper
									googleInputProps={googleInputPropsBirthField}
									inputProps={{
										fullWidth: true,
										label: t("raFilling.billingOwnerInfo.form.label.birthPlace"),
										id: 'birthPlace',
										name: `billingOwnerInfo.${type}.person.birthPlace`,
										type: 'text',
										value: value || '',
										disabled: forceReadOnly || !editable,
										onChange: (e) => {
											onChange(e.target.value)
											if (!e.target.value) {
												handleResetBirthFields()
											}
										}
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.birthNation`}
							label={t("raFilling.billingOwnerInfo.form.label.birthNation")}
							control={raFillingDataForm.control}
							options={countriesList}
							getOptionLabel={(opt: any) => t(countriesList.find((x:any)=>x.code === opt.code)?.description)}
							isOptionEqualToValue={(option, value) => option?.code === value?.code}
							disabled={(forceReadOnly || !editable) && billingPerson.birthNation}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.countryResidence`}
							label={t("raFilling.billingOwnerInfo.form.label.residenceCountry")}
							control={raFillingDataForm.control}
							options={countriesList}
							disabled={(forceReadOnly || !editable) && billingPerson.countryResidence}
							getOptionLabel={(opt: any) => countriesList.find((x: any) => x.code === opt.code)?.description}
							isOptionEqualToValue={(option, value) => option?.code === value?.code}
							onChange={(_e: React.SyntheticEvent, val) => handleResidenceCountryChange(val?.code || '')}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.drivingLicenseNumber`}
							label={t("raFilling.billingOwnerInfo.form.label.drivingLicenseNumber")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.drivingLicenseNumber}
							onChange={(e) => setValueCheck('drvLicenceNumber', e.target.value)}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.firstEmail`}
							label={t("raFilling.billingOwnerInfo.form.label.email")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.firstEmail}
							onChange={e => setValueCheck('email', e.target.value)}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.prefix`}
							label={t("raFilling.billingOwnerInfo.form.label.mobilePrefix")}
							options={mobilePrefixesQuery?.data ?? []}
							isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
							getOptionLabel={(opt: any) => `+${opt?.description} (${opt?.code})`}
							disabled={(forceReadOnly || !editable) && billingPerson.prefix}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.mobile`}
							label={t("raFilling.billingOwnerInfo.form.label.mobile")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.mobile}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.certifiedEmail`}
							label={t("raFilling.billingOwnerInfo.form.label.certifiedEmail")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.certifiedEmail}
						/>
					</Grid>

				</Grid>
				<Grid item container xs={12} columnSpacing={2} sx={{mb: 'auto'}}>
					<Grid item xs={11} md={5} >
						<Controller
							name={`billingOwnerInfo.${type}.person.billingAddressGoogleSearch`}
							control={raFillingDataForm.control}
							render={({field: {onChange, value}}: any) => (
								<GoogleAutocompleteWrapper
									googleInputProps={googleInputPropsBillingField}
									inputProps={
										{
											fullWidth: true,
											label: t('raFilling.billingOwnerInfo.form.label.googleFilterAddress'),
											id: 'billingAddressGoogleSearch',
											name: `billingOwnerInfo.${type}.person.billingAddressGoogleSearch`,
											type: 'text',
											value: value || '',
											onChange: (e) => {
												onChange(e.target.value)

												if (!e.target.value) {
													handleResetBillingAddressFields();
												}
											},
											disabled: forceReadOnly || !editable
										}
									}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={1} alignItems="center" sx={{textAlign: 'center', marginTop: 'auto'}}>
						<Tooltip arrow placement="top" title={t("raFilling.billingOwnerInfo.button.resetBillingAddressValue")}>
							<IconButton onClick={handleResetBillingAddressFields}
										disabled={forceReadOnly || !editable}
							>
								<DeleteRounded fontSize="small" />
							</IconButton>
						</Tooltip>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.billingCountry`}
							label={t("raFilling.billingOwnerInfo.form.label.billingCountry")}
							control={raFillingDataForm.control}
							options={countriesList}
							getOptionLabel={(opt: any) => t(countriesList.find((x:any)=>x.code === opt.code)?.description)}
							isOptionEqualToValue={(option, value) => option?.code === value?.code}
							onChange={(_e: React.SyntheticEvent) => handleBillingCountryChange()}
							disabled={(forceReadOnly || !editable) && billingPerson.billingCountry}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.billingProvince`}
							label={t("raFilling.billingOwnerInfo.form.label.billingProvince")}
							control={raFillingDataForm.control}
							options={provincesQuery.data ?? []}
							loading={provincesQuery.isInitialLoading}
							getOptionLabel={(opt) => opt?.description}
							isOptionEqualToValue={(option: any, value: any) => option?.codeProvince === value?.codeProvince}
							required={billingCountry?.code === ITALY_CODE}
							disabled={(forceReadOnly || !editable) && billingPerson.billingProvince}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.billingCity`}
							label={t("raFilling.billingOwnerInfo.form.label.billingCity")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && billingPerson.billingCity}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.billingAddress`}
							label={t("raFilling.billingOwnerInfo.form.label.billingAddress")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.billingAddress}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.billingStreetNumber`}
						    label={t("raFilling.billingOwnerInfo.form.label.billingStreetNumber")}
						    control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.billingStreetNumber}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.person.billingPostalCode`}
							label={t("raFilling.billingOwnerInfo.form.label.billingPostalCode")}
							control={raFillingDataForm.control}
							disabled={(forceReadOnly || !editable) && billingPerson.billingPostalCode}
						/>
					</Grid>
				</Grid>
			</Grid>
		</>
	);
}

export default BillingPersonData;
